import React from 'react'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'

import { ListTile, Spinner, Animation, Button } from 'components'
import { ItemBlock, SectionBlock, SortableForest, ItemInput, SectionInput } from 'features'
import { AccessModule } from 'modules'

import { Section, Item } from 'schema'
import { itemContext, sectionContext } from 'context'
import { filterCheck, sortItems, groupItems, checkPageLoaded } from 'utils'
import * as actionCreators from 'store/actions'
import { AnimatePresence } from 'framer-motion'

const Container = styled.div`
    height: 100%;
    flex-grow: 4;
    overflow: hidden;
    padding-left: ${props => props.full_width ? 'var(--main-padding-width-small)' : 'var(--main-padding-width)'};
    padding-right: ${props => props.full_width ? 'var(--main-padding-width-small)' : 'var(--main-padding-width)'};
    margin-bottom: 50vh;
    @media (max-width: 1024px) {
        padding-left: var(--main-padding-width-small);
        padding-right: var(--main-padding-width-small);
    }
`

const Placeholder = styled.div`
    border-radius: 5px;
    padding: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--secondary-color);
`

const ListView = props => {
    const {

    } = props;
    const debug = false;
    const dispatch = useDispatch()
    const data = useSelector(state => state.db.present.data && state.db.present.data.toJS())
    const user = useSelector(state => state.auth.user)
    const inputID = useSelector(state => state.content.inputID)
    const modalID = useSelector(state => state.content.modalID)
    const sessionId = useSelector(state => state.content.sessionId)
    const showAccess = useSelector(state => state.content.showAccess)
    const loadedPages = useSelector(state => state.db.present.loadedPages)
    const pageID = user['activePageID']
    const page = data['pages'][pageID]
    const deferred = useSelector(state => state.db.present.deferred)
    const deferredLoaded = useSelector(state => state.db.present.deferredLoaded)
    const isPageLoaded = checkPageLoaded(pageID, user, loadedPages)
    const limit = 20
    const [sectionLimits, setLimits] = React.useState(() => {
        let defaultLimits = {}
        page['sections'].forEach(sectionID => { defaultLimits[sectionID] = limit })
        return defaultLimits;
    })

    React.useEffect(() => {
        const check = checkPageLoaded(pageID, user, loadedPages)
        if(!check['loaded'] && deferredLoaded){
            console.log('[LIST_VIEW] Need to query page data', check['toLoad'])
            dispatch(actionCreators.loadPage(pageID, check['toLoad'].join(','), data))
        } else {
            console.log('[no need reload]')
        }
    }, [pageID, user['show_completed']])

    const renderDragOverlay = (id) => {
        return {
            item: <ItemBlock itemID={id} type='DRAG_OVERLAY'/>,
            container: <SectionBlock section={data['sections'][id]} type='DRAG_OVERLAY'/>
        }
    }

    const onDrop = (values) => {
        console.log('[LISTVIEW] onDrop', values)
        const disableDrop = false
        if(values['type'] === 'ITEM'){
            !disableDrop && dispatch(actionCreators.commitDND2({...values, data, userId: user['uid'], sessionId}))
        } else if (values['type'] === 'CONTAINER') {
            let updatedPage = {...page}
            updatedPage['sections'] = values['containers']
            !disableDrop && dispatch(actionCreators.updatePage(updatedPage, data, user['uid']))
        } else if (values['type'] === 'CALENDAR') {
            const schedule = values['schedule']
            let updatedItem = new Item({
                ...data['items'][values['activeId']],
                start_date: schedule['start_date'],
                end_date: schedule['end_date'],
                start_time: schedule['view'] !== 'month' ? schedule['start_time'] : null,
                end_time: schedule['view'] !== 'month' ? schedule['end_time'] : null,
            })
            !disableDrop && dispatch(actionCreators.updateItem(updatedItem, data, user['uid'], sessionId))
        }
    }

    const buildView = () => {
        // console.log('[buildView]')
        const listID = 'uuidv4()'
        const filters = page['filters']
        const sort = page['sort']
        const group = page['group']
        const isFiltered = filters && Object.values(filters).some(value => value)
        const isSorted = sort && sort['value'] && sort['direction']
        const isGrouped = group && Object.keys(group).length > 0
        const disableDND = isFiltered || isSorted || isGrouped || inputID || modalID || !deferredLoaded

        // Get sections
        let sectionIDs = page['sections'].filter(sectionID => data['sections'][sectionID] && data['sections'][sectionID]['status'] === sectionContext.STATUS.ACTIVE)
        // user['show_archived'] && sectionIDs.push(...page['sections'].filter(sectionID => data['sections'][sectionID] && data['sections'][sectionID]['status'] === sectionContext.STATUS.ARCHIVE))
        const sections = sectionIDs.map(sectionID => data['sections'][sectionID])

        // Construct trees
        let trees = []
        if(sections.length > 0){
            sections.forEach((section, index) => {

                // Render items
                const sectionItems = new Section(section).buildView(false, data, user['show_completed']) || {}
                let sortedItems = isSorted ? sortItems(sort, Object.values(sectionItems)) : Object.values(sectionItems)
                let blockCount = 0
                let itemCount = 0
                let lastParents = []
                let afterTree = []
    
                const filteredItems = !section['items_visibility'] ? [] : Object.values(sortedItems).filter(item => {
                    const filterInclude = !filters ? true 
                                        : Object.keys(filters).length === 0 ? true
                                        : filterCheck(filters, item)
                    const toInclude = blockCount + 1 > sectionLimits[section['sectionID']] ? false
                                    : item['status'] === itemContext.STATUS.INCOMPLETE && filterInclude ||
                                    (filterInclude && user['show_completed'] && !process.env.process.env.REACT_APP_DISABLE_COMPLETED
                                    && item['status'] !== itemContext.STATUS.INCOMPLETE 
                                    && blockCount < sectionLimits[section['sectionID']])
                    if(toInclude){
                        if(item['status'] === itemContext.STATUS.INCOMPLETE){
                            lastParents = [...item['parents'], item['itemID']]
                        }
                        blockCount += 1
                    }
                    itemCount += 1
                    return toInclude
                })
    
                const tree = filteredItems.map(item => {
                    const disableDrag = disableDND || item['status'] === itemContext.STATUS.COMPLETED
                    return {
                        id: item['itemID'],
                        parentId: item['parents'] && item['parents'].slice(-1)[0],
                        block: <ItemBlock itemID={item['itemID']} disableDrag={disableDrag} showDivider={user['item_divider']}/>,
                        depth: item['parents'].length,
                        disableDrag: disableDrag,
                        parents: item['parents'],
                        containerId: section['sectionID']
                    }
                })
    
                // Render ItemInput
                const sectionLimit = sectionLimits[section['sectionID']]
                // const showSpinner = (deferred && deferred.sectionIDs && deferred.sectionIDs.includes(section['sectionID'])) | (!deferredLoaded)
                const showSpinner = (deferred && deferred.sectionIDs && deferred.sectionIDs.includes(section['sectionID']))
                section['items_visibility'] &&
                afterTree.push(
                    <div>
                        {showSpinner ? <Spinner styles={{width: '100%', margin: '0.5rem 0 0 0', color: 'var(--secondary-color)'}}/> : null}
                        {blockCount === sectionLimit && itemCount > sectionLimit &&
                        <Button 
                            text={`Show ${itemCount - sectionLimit < limit ? itemCount - sectionLimit : 20} more`}
                            styles={{margin: '0.5rem 0', backgroundColor: 'var(--secondary-color)'}}
                            onSelect={() => setLimits({...sectionLimits, [section['sectionID']]: sectionLimit + limit })}
                            />}
                        <ItemInput key={section['sectionID'] + '_ItemInput'}  
                            id={section['sectionID'] + '_ItemInput'}  
                            pageID={pageID} sectionID={section['sectionID']} 
                            parents={lastParents} enableTab/>
                    </div>
                )

                // Render SectionInput
                afterTree.push(<SectionInput key={section['sectionID'] + '_SectionInput'} 
                                id={section['sectionID'] + '_SectionInput'}
                                type={inputID === `${section['sectionID']}_SectionInput` && 'CREATE'}/>)

                trees.push({
                    id: section['sectionID'],
                    tree: tree,
                    visibility: section['items_visibility'],
                    // beforeTree: index !== 0 ? <SectionBlock section={section} disableShowMore={index === 0} hideGrip={index === 0 || disableDND}/> : null,
                    beforeTree: <SectionBlock section={section} hideGrip={disableDND}/>,
                    afterTree: afterTree,
                    block: <SectionBlock hideGrip section={section} disableShowMore={index === 0}/>,
                    disableDrag: disableDND || section['status'] === sectionContext.STATUS.ARCHIVE,
                    // disableContainerDrag: index === 0 || disableDND,
                    containerId: pageID
                })
                
            })

            debug && console.log('[LISTVIEW]', {trees})
        } 
        
        else {
            trees.push({
                id: 'PLACEHOLDER',
                tree: [],
                afterTree: <SectionInput type='BUTTON' styles={{height: 'min-content'}}/>,
            })
        }
        console.log({
            disableDND,
            isFiltered,
            isSorted,
            isGrouped,
            inputID,
            modalID,
            deferredLoaded
        })
        return <SortableForest key='LIST_FOREST' id='LIST_FOREST'
                    trees={trees} dragOverlay={renderDragOverlay} onDrop={onDrop}  
                    disableDND={disableDND} disableVirtual={true}
                    secondaryContainer={<AccessModule/>} showSecondary={showAccess}
                    styles={{treeWidth: '100%', treeHeight: 'auto', margin: '0 0 1rem 0'}}
                    dragStyles={{ padding: '0.5rem 0.5rem 0 0.5rem' }}/>
    }

    const buildGroupView = () => {
        const pageGroup = data['pages'][pageID]['group']
        const groups = groupItems(pageID, data, user)
        const statuses = process.env.process.env.REACT_APP_DISABLE_COMPLETED ? ['INCOMPLETE']
                        : user['show_completed'] ? ['INCOMPLETE','COMPLETED'] : ['INCOMPLETE']

        const trees = Object.values(groups).map(group => {
            const items = [...group['INCOMPLETE'], ...group['COMPLETED']]
            let tree = []
            let blockCount = 0
            let itemCount = 0
            const sectionLimit = sectionLimits[group['id']] || 20

            items.forEach(item => {
                itemCount += 1
                if(statuses.includes(item['status']) && blockCount + 1 <= sectionLimit){
                    blockCount += 1
                    tree.push({
                        id: item['itemID'],
                        parentId: item['parents'] && item['parents'].slice(-1)[0],
                        block: <ItemBlock itemID={item['itemID']} disableDrag showDivider={user['item_divider']}/>,
                        depth: item['parents'].length,
                        disableDrag: true,
                        parents: item['parents'],
                        containerId: group['id']
                    }) 
                }
            })

            return {
                id: group['id'],
                containerId: pageID,
                disableDrag: true,
                tree: tree,
                beforeTree: <ListTile text={group['title']} icon={group['icon']} 
                                styles={{ 
                                    height: '2.5rem', backgroundColor: 'transparent', 
                                    borderBottom: '1px solid var(--primary-accent-color)', borderRadius: 0 
                                }}/>,
                afterTree: 
                <div>
                    {!deferredLoaded ? <Spinner styles={{width: '100%', margin: '0.5rem 0 0 0', color: 'var(--secondary-color)'}}/> : null}
                    {blockCount === sectionLimit && itemCount > sectionLimit &&
                    <Button 
                        text={`Show ${itemCount - sectionLimit < limit ? itemCount - sectionLimit : 20} more`}
                        styles={{margin: '0.5rem 0.1rem', backgroundColor: 'var(--secondary-color)'}}
                        onSelect={() => {
                            setLimits({...sectionLimits, [group['id']]: sectionLimit + limit })
                        }}
                        />}
                    <ItemInput id={group['id'] + '_ItemInput'} sectionSelector
                        pageID={pageID} 
                        sectionID={page['sections'][0]} 
                        parents={[]} 
                        priority={pageGroup && pageGroup === 'PRIORITY' ? group['id'] : null}
                        tags={pageGroup && pageGroup === 'TAGS' ? [group['id']] : null}
                        start_date={pageGroup && pageGroup === 'START_DATE' ? group['id'] : null}
                        end_date={pageGroup && pageGroup === 'END_DATE' ? group['id'] : null}/>
                </div>
                
            }
        })
        return <SortableForest key='GROUP' trees={trees} dragOverlay={renderDragOverlay} onDrop={onDrop} 
                        disableDND={true} disableVirtual={false}
                        secondaryContainer={<AccessModule/>} showSecondary={showAccess}
                        styles={{treeWidth: '100%', treeHeight: 'auto', margin: '0 0 1rem 0'}}/>        
    }

    if(isPageLoaded['loaded'] || !deferredLoaded){
        return (
            <Container full_width={user['full_width']} id='ListView_Container'>
                <AnimatePresence>
                    <Animation animation='default' duration={0.5}>
                        {page['group'] ? buildGroupView() : buildView()}
                    </Animation>
                </AnimatePresence>
            </Container>
        )
    } else  {
        return (
            <Container full_width={user['full_width']} id='ListView_Container'>
                <Spinner styles={{ margin: '0 0 0 0.5rem' }}/>
            </Container>
        )
    }
}

export default ListView